import React, { useEffect, useState, useRef, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from 'react-hot-toast';
import { useNotifications } from './hooks/useNotifications';
import { doc, getDoc } from 'firebase/firestore';
import { db } from './firebase/config';
import { getPostLoginRoute, checkRouteAccess } from './utils/routing';
import useBilling from './hooks/useBilling';
import { isAgencyOnboardingRequired } from './utils/onboardingHelper';

// Components
import LoadingSpinner from './components/LoadingSpinner';
import WebsiteLayout from './components/WebsiteLayout';
import ErrorBoundary from './components/ErrorBoundary';
import CookieConsent from './components/CookieConsent';

// Pages
import Welcome from './pages/Welcome';
import Login from './pages/Login';
import Register from './pages/Register';
import LandlordTypeSelection from './pages/LandlordTypeSelection';
import SimpleLogin from './pages/SimpleLogin';
import UserTypeSelectionLogin from './pages/UserTypeSelectionLogin';
import ForgotPassword from './pages/ForgotPassword';
import AuthAction from './pages/AuthAction';
import NotFound from './pages/NotFound';

// Tenant Pages - Exact iOS app screens
import HomeScreen from './pages/tenant/HomeScreen';
import FavorietenScreen from './pages/tenant/FavorietenScreen';
import BerichtenScreen from './pages/tenant/MessagesNew';
import AccountOverviewScreen from './pages/tenant/AccountOverviewScreen';
import TenantAgenda from './pages/tenant/AgendaNew';
import MijnPandScreen from './pages/tenant/MijnPandScreen';
import TenantContracts from './pages/tenant/TenantContracts';
import TenantViewings from './pages/tenant/TenantViewings';
import PropertyDetail from './pages/PropertyDetail';
import Chat from './pages/Chat';

// Other pages (eagerly loaded - used in unauthenticated routes)
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import CookiePolicy from './pages/CookiePolicy';
import LegalDocuments from './pages/LegalDocuments';
import PolicyViewer from './pages/PolicyViewer';
import EmailVerification from './pages/EmailVerification';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentCancel from './pages/PaymentCancel';
import ComingSoon from './pages/ComingSoon';
import ComingSoonLandlord from './pages/ComingSoonLandlord';
import ComingSoonPartnerships from './pages/ComingSoonPartnerships';
import VerhuurderInfo from './pages/VerhuurderInfo';
import About from './pages/About';
import Contact from './pages/Contact';

// Lazy-loaded pages (code splitting)
const VerhuurderOverviewScreen = lazy(() => import('./pages/landlord/VerhuurderOverviewScreen'));
const VerhuurdePanden = lazy(() => import('./pages/landlord/VerhuurdePanden'));
const LandlordProperties = lazy(() => import('./pages/landlord/Properties'));
const LandlordMessages = lazy(() => import('./pages/landlord/MessagesNew'));
const LandlordContracts = lazy(() => import('./pages/landlord/Contracts'));
const LandlordViewings = lazy(() => import('./pages/landlord/ViewingsNew'));
const PropertyViewings = lazy(() => import('./pages/landlord/PropertyViewings'));
const LandlordAgenda = lazy(() => import('./pages/landlord/AgendaNew'));
const CRMIntegration = lazy(() => import('./pages/landlord/CRMIntegration'));
const EditProperty = lazy(() => import('./pages/landlord/EditProperty'));
const LandlordProfile = lazy(() => import('./pages/landlord/Profile'));
const LandlordOnboarding = lazy(() => import('./pages/landlord/LandlordOnboarding'));
const Statistics = lazy(() => import('./pages/landlord/Statistics'));
const Billing = lazy(() => import('./pages/landlord/Billing'));
const CRMGuide = lazy(() => import('./pages/landlord/CRMGuide'));
const CreateContract = lazy(() => import('./pages/landlord/CreateContractNew'));
const EditContract = lazy(() => import('./pages/landlord/EditContract'));
const ContractDetail = lazy(() => import('./pages/landlord/ContractDetail'));
const AmendContract = lazy(() => import('./pages/landlord/AmendContract'));
const ReplaceCustomContract = lazy(() => import('./pages/landlord/ReplaceCustomContract'));
const TenantContractDetail = lazy(() => import('./pages/tenant/ContractDetail'));
const AmendmentReview = lazy(() => import('./pages/tenant/AmendmentReview'));
const AgencyDashboard = lazy(() => import('./pages/landlord/AgencyDashboard'));
const AgencyOperationsDashboard = lazy(() => import('./pages/landlord/AgencyOperationsDashboard'));
const AgencyOnboarding = lazy(() => import('./pages/landlord/AgencyOnboarding'));
const ClientDashboard = lazy(() => import('./pages/landlord/ClientDashboard'));
const AgencyAddPropertyForClient = lazy(() => import('./pages/landlord/AgencyAddPropertyForClient'));
const AgencyInviteAcceptance = lazy(() => import('./pages/landlord/AgencyInviteAcceptance'));
const CRMDraftEdit = lazy(() => import('./pages/landlord/CRMDraftEdit'));
const PropertyDetailLandlord = lazy(() => import('./pages/landlord/PropertyDetailLandlord'));
const PrivateAgencyFollow = lazy(() => import('./pages/landlord/PrivateAgencyFollow'));
const MijnImmo = lazy(() => import('./pages/landlord/MijnImmo'));
const AgencyProperties = lazy(() => import('./pages/landlord/AgencyProperties'));
const AgencyInbox = lazy(() => import('./pages/landlord/AgencyInbox'));
const AgencyViewings = lazy(() => import('./pages/landlord/AgencyViewings'));
const AgencyCandidates = lazy(() => import('./pages/landlord/AgencyCandidates'));
const SimpleAddProperty = lazy(() => import('./pages/landlord/SimpleAddProperty'));
const DeleteAccount = lazy(() => import('./pages/DeleteAccount'));

/**
 * Protected Route Component with STRICT role-based routing
 * 
 * CRITICAL RULES:
 * - Agencies NEVER access private landlord routes
 * - Private landlords NEVER access agency routes
 * - Missing landlordType redirects to selection screen
 */
function ShortPropertyRedirect() {
  const { id } = useParams();
  return <Navigate to={`/property/${id}`} replace />;
}

function ProtectedRoute({ children, userType, landlordType: requiredLandlordType }) {
  const { user, userType: currentUserType, landlordType: currentLandlordType, loading } = useAuth();
  const {
    hasSelectedPlan,
    plan: billingPlan,
    loading: billingLoading
  } = useBilling();
  const location = useLocation();
  const navigate = useNavigate();
  const lastLoginAt = Number(sessionStorage.getItem('lastLoginAt') || 0);
  const isPostLoginGraceWindow = lastLoginAt > 0 && (Date.now() - lastLoginAt) < 2500;
  const [planGateArmed, setPlanGateArmed] = useState(false);
  const [agencyProfileCheck, setAgencyProfileCheck] = useState({ loading: false, required: false });

  const shouldEvaluatePrivatePlanGate =
    currentUserType === 'landlord' &&
    currentLandlordType === 'private' &&
    location.pathname.startsWith('/landlord') &&
    !location.pathname.startsWith('/landlord/agency');
  const planRequiredAllowedRoutes = [
    '/landlord/billing',
    '/email-verification',
    '/landlord-type',
    '/landlord/onboarding',
    '/landlord/agency/onboarding'
  ];
  const shouldApplyPrivatePlanGate =
    shouldEvaluatePrivatePlanGate &&
    !planRequiredAllowedRoutes.includes(location.pathname);
  const agencyProfileAllowedRoutes = [
    '/landlord/agency/onboarding',
    '/landlord/agency/billing',
    '/email-verification',
    '/landlord-type'
  ];
  const shouldCheckAgencyProfile =
    currentUserType === 'landlord' &&
    currentLandlordType === 'agency' &&
    !agencyProfileAllowedRoutes.includes(location.pathname);

  useEffect(() => {
    if (!shouldApplyPrivatePlanGate) {
      setPlanGateArmed(false);
      return;
    }
    if (billingLoading || isPostLoginGraceWindow || hasSelectedPlan) {
      setPlanGateArmed(false);
      return;
    }

    // Delay hard redirect a bit to avoid visible flicker while billing snapshots settle.
    const timer = setTimeout(() => setPlanGateArmed(true), 1200);
    return () => clearTimeout(timer);
  }, [shouldApplyPrivatePlanGate, billingLoading, isPostLoginGraceWindow, hasSelectedPlan]);

  useEffect(() => {
    let isMounted = true;
    const checkAgencyProfile = async () => {
      if (!shouldCheckAgencyProfile || !user?.uid) {
        if (isMounted) setAgencyProfileCheck({ loading: false, required: false });
        return;
      }
      if (isMounted) setAgencyProfileCheck({ loading: true, required: false });
      try {
        const userSnap = await getDoc(doc(db, 'users', user.uid));
        const userData = userSnap.exists() ? userSnap.data() : {};
        const required = isAgencyOnboardingRequired(userData, user.email);
        if (isMounted) setAgencyProfileCheck({ loading: false, required });
      } catch (error) {
        console.error('ProtectedRoute: Agency profile check failed', error);
        if (isMounted) setAgencyProfileCheck({ loading: false, required: false });
      }
    };
    checkAgencyProfile();
    return () => { isMounted = false; };
  }, [shouldCheckAgencyProfile, user?.uid, user?.email]);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!user) {
    return <Navigate to="/welcome" replace />;
  }

  if (shouldCheckAgencyProfile && agencyProfileCheck.loading) {
    return <LoadingSpinner />;
  }
  if (shouldCheckAgencyProfile && agencyProfileCheck.required) {
    return <Navigate to="/landlord/agency/onboarding" replace />;
  }

  if (shouldApplyPrivatePlanGate) {
    if (billingLoading || isPostLoginGraceWindow || (!hasSelectedPlan && !planGateArmed)) {
      return <LoadingSpinner />;
    }
    if (!hasSelectedPlan) {
      return <Navigate to="/landlord/billing?required=1&plan=starter&source=plan-required" replace />;
    }
  }

  // Listing-Only (private) restrictions: no in-app chat, contracts, viewings, agenda.
  const isPrivateListingOnly =
    currentUserType === 'landlord' &&
    currentLandlordType !== 'agency' &&
    billingPlan === 'listing_only_private';
  const listingOnlyRestrictedPaths = [
    '/landlord/messages',
    '/landlord/contracts',
    '/landlord/viewings',
    '/landlord/agenda',
    '/landlord/verhuurde-panden',
    '/landlord/immo'
  ];
  if (isPrivateListingOnly && listingOnlyRestrictedPaths.some((p) => location.pathname.startsWith(p))) {
    return (
      <div className="relative">
        {children}
        <div className="absolute inset-0 z-50 bg-transparent flex items-center justify-center p-4">
          <div className="max-w-md w-full rounded-2xl border border-blue-200 bg-white shadow-xl p-6 text-center">
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Pro vereist voor deze functie</h2>
            <p className="text-sm text-slate-600 mb-5">
              Je zit op Listing-Only. Deze functie blijft zichtbaar, maar je hebt Pro nodig om ze te gebruiken.
            </p>
            <button
              onClick={() => {
                navigate('/landlord/billing?required=1&plan=pro&source=listing-only-gate&autocheckout=1');
              }}
              className="w-full h-11 rounded-lg bg-[#5EABEB] text-white font-semibold hover:bg-[#4A9FE6] transition-colors"
            >
              Upgrade naar Pro
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Listing-Only (agency) restrictions: no client management.
  const isAgencyListingOnly =
    currentUserType === 'landlord' &&
    currentLandlordType === 'agency' &&
    billingPlan === 'listing_only_agency';
  const agencyListingOnlyRestrictedPaths = [
    '/landlord/agency/clients'
  ];
  if (isAgencyListingOnly && agencyListingOnlyRestrictedPaths.some((p) => location.pathname.startsWith(p))) {
    return (
      <div className="relative">
        {children}
        <div className="absolute inset-0 z-50 bg-transparent flex items-center justify-center p-4">
          <div className="max-w-md w-full rounded-2xl border border-blue-200 bg-white shadow-xl p-6 text-center">
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Agency abonnement vereist</h2>
            <p className="text-sm text-slate-600 mb-5">
              Je zit op Listing-Only. De clientfunctie is enkel beschikbaar met een Agency abonnement.
            </p>
            <button
              onClick={() => {
                navigate('/landlord/agency/billing?required=1&plan=agency&source=listing-only-gate&autocheckout=1');
              }}
              className="w-full h-11 rounded-lg bg-[#5EABEB] text-white font-semibold hover:bg-[#4A9FE6] transition-colors"
            >
              Upgrade naar Agency
            </button>
          </div>
        </div>
      </div>
    );
  }

  // If no userType is specified, allow access (public routes)
  if (!userType) {
    return children;
  }

  // Special routes that don't require landlordType (during registration/onboarding)
  const allowedWithoutLandlordType = [
    '/landlord/onboarding',
    '/landlord/agency/onboarding',
    '/landlord-type',
    '/email-verification'
  ];
  const isAllowedRoute = allowedWithoutLandlordType.includes(location.pathname);

  // Use central routing logic to check access
  const accessCheck = checkRouteAccess(location.pathname, currentUserType, currentLandlordType);
  
  // If access is denied, redirect
  if (!accessCheck.allowed) {
    return <Navigate to={accessCheck.redirectTo || '/welcome'} replace />;
  }

  // If userType is specified, check if it matches
  if (currentUserType !== userType) {
    
    // Use central routing to determine redirect
    if (currentUserType === 'tenant') {
      return <Navigate to="/" replace />;
    } else if (currentUserType === 'landlord') {
      // Fetch user doc to use getPostLoginRoute
      getDoc(doc(db, 'users', user.uid)).then(userDoc => {
        if (userDoc.exists()) {
          const route = getPostLoginRoute(userDoc.data(), user.email);
          return <Navigate to={route} replace />;
        }
      });
      // Fallback redirect
      if (!currentLandlordType && !isAllowedRoute) {
        return <Navigate to="/landlord-type" replace />;
      }
      if (currentLandlordType === 'agency') {
        return <Navigate to="/landlord/agency/dashboard" replace />;
      } else if (currentLandlordType === 'private') {
        return <Navigate to="/landlord" replace />;
      }
    } else {
      return <Navigate to="/user-type-login" replace />;
    }
  }

  // STRICT landlordType enforcement
  if (requiredLandlordType && currentUserType === 'landlord') {
    // Allow onboarding routes even without landlordType
    if (!currentLandlordType && !isAllowedRoute) {
      
      return <Navigate to="/landlord-type" replace />;
    }
    
    // CRITICAL: Enforce strict landlordType matching
    if (currentLandlordType && currentLandlordType !== requiredLandlordType) {
      
      // Redirect based on actual landlord type
      if (currentLandlordType === 'agency') {
        return <Navigate to="/landlord/agency/dashboard" replace />;
      } else if (currentLandlordType === 'private') {
        return <Navigate to="/landlord" replace />;
      }
    }
  }

  return children;
}

// Main App Routes
function AppRoutes() {
  const { user, userType, landlordType, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Timeout: if userType stays null for >6s after loading finishes,
  // redirect to user-type selection instead of spinning forever.
  const [userTypeTimedOut, setUserTypeTimedOut] = useState(false);
  const userTypeTimeoutRef = useRef(null);

  useEffect(() => {
    if (loading || userType || !user) {
      clearTimeout(userTypeTimeoutRef.current);
      setUserTypeTimedOut(false);
      return;
    }
    userTypeTimeoutRef.current = setTimeout(() => setUserTypeTimedOut(true), 6000);
    return () => clearTimeout(userTypeTimeoutRef.current);
  }, [loading, userType, user]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <Routes>
        <Route path="/" element={<Welcome />} />
        <Route path="/welcome" element={<Welcome />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/user-type-selection" element={<Navigate to="/user-type-login" replace />} />
        <Route path="/user-type" element={<Navigate to="/user-type-login" replace />} />
        <Route path="/landlord-type" element={<LandlordTypeSelection />} />
        <Route path="/simple-login" element={<SimpleLogin />} />
        <Route path="/user-type-login" element={<UserTypeSelectionLogin />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/auth/action" element={<AuthAction />} />
        <Route path="/agency-invite/:inviteId" element={<AgencyInviteAcceptance />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/cookies" element={<CookiePolicy />} />
        <Route path="/legal" element={<LegalDocuments />} />
        <Route path="/legal/:policyId" element={<PolicyViewer />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/property/:id" element={<PropertyDetail />} />
        <Route path="/p/:id" element={<ShortPropertyRedirect />} />
        <Route path="/coming-soon" element={<ComingSoon />} />
        <Route path="/coming-soon/landlord" element={<ComingSoonLandlord />} />
        <Route path="/coming-soon/partnerships" element={<ComingSoonPartnerships />} />
        <Route path="/voor-verhuurders" element={<VerhuurderInfo />} />
        <Route path="/about" element={<About />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    );
  }

  if (user && !user.emailVerified) {
    if (location.pathname === '/email-verification' || location.pathname === '/auth/action') {
      return (
        <Routes>
          <Route path="/email-verification" element={<EmailVerification />} />
          <Route path="/auth/action" element={<AuthAction />} />
        </Routes>
      );
    }
    return (
      <Routes>
        <Route path="/email-verification" element={<EmailVerification />} />
        <Route path="/auth/action" element={<AuthAction />} />
        <Route path="*" element={<Navigate to="/email-verification" replace />} />
      </Routes>
    );
  }

  if (!userType) {
    // If on /landlord-type, show that selection screen (legitimate flow).
    if (location.pathname === '/landlord-type') {
      return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center">
          <Routes>
            <Route path="/landlord-type" element={<LandlordTypeSelection />} />
            <Route path="*" element={<Navigate to="/landlord-type" replace />} />
          </Routes>
        </div>
      );
    }
    // Timed out waiting for Firestore — redirect to the proper login flow
    // (UserTypeSelectionLogin) which handles type selection + auth in one page.
    if (userTypeTimedOut) {
      return <Navigate to="/user-type-login" replace />;
    }
    // Still resolving — show spinner.
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  // If userType is landlord but no landlordType, show landlord-type selection
  // BUT: Only redirect if we're done loading (context must be fully loaded)
  // AND: Only redirect if we're NOT already on an allowed route (to prevent loops)
  if (userType === 'landlord' && !landlordType && !loading) {
    // Allow access to these routes even without landlordType (during registration/onboarding)
    const allowedRoutes = [
      '/landlord-type',
      '/landlord/onboarding',
      '/landlord/agency/onboarding',
      '/email-verification',
      '/user-type-login'
    ];
    
    if (allowedRoutes.includes(location.pathname)) {
      // If on allowed route, don't redirect (prevent loop)
      if (location.pathname === '/landlord-type') {
        return (
          <Routes>
            <Route path="/landlord-type" element={<LandlordTypeSelection />} />
            <Route path="*" element={<Navigate to="/landlord-type" replace />} />
          </Routes>
        );
      }
      // For other allowed routes, continue to normal routing (don't redirect)
      // This prevents infinite loops
    } else {
      // For other routes, redirect to landlord-type selection
      return <Navigate to="/landlord-type" replace />;
    }
  }

  // Auto-redirect to appropriate dashboard after login - IMMEDIATE redirect
  // BUT: Don't redirect if we're on email-verification (to prevent loops)
  if (user && userType && !loading && location.pathname !== '/email-verification') {
    // If on login/welcome/user-type-selection pages, redirect immediately
    if (location.pathname === '/welcome' || 
        location.pathname === '/login' || 
        location.pathname === '/user-type-selection' ||
        location.pathname === '/user-type' ||
        location.pathname === '/user-type-login') {
      if (userType === 'tenant') {
        return <Navigate to="/" replace />;
      } else if (userType === 'landlord') {
        if (!landlordType) {
          return <Navigate to="/landlord-type" replace />;
        } else if (landlordType === 'agency') {
          return <Navigate to="/landlord/agency/dashboard" replace />;
        } else {
          return <Navigate to="/landlord" replace />;
        }
      }
    }
  }
  
  // Also handle root path redirect for authenticated users
  if (user && userType && location.pathname === '/') {
    if (userType === 'landlord') {
      // ALWAYS redirect landlords to landlord type selection if they haven't chosen yet
      if (!landlordType) {
        return <Navigate to="/landlord-type" replace />;
      } else if (landlordType === 'agency') {
        return <Navigate to="/landlord/agency-dashboard" replace />;
      } else {
        return <Navigate to="/landlord" replace />;
      }
    }
    // Tenant stays on root path, which is correct
  }
  
  return (
    <WebsiteLayout>
      <Suspense fallback={<LoadingSpinner />}>
      <Routes>
        {/* Landlord Type Selection - Must be accessible for landlords without landlordType */}
        <Route path="/landlord-type" element={<LandlordTypeSelection />} />
        
        {/* Tenant Routes */}
        <Route path="/" element={
          <ProtectedRoute userType="tenant">
            <HomeScreen />
          </ProtectedRoute>
        } />
        <Route path="/favorites" element={
          <ProtectedRoute userType="tenant">
            <FavorietenScreen />
          </ProtectedRoute>
        } />
        <Route path="/properties" element={
          <ProtectedRoute userType="tenant">
            <HomeScreen />
          </ProtectedRoute>
        } />
        <Route path="/messages" element={
          <ProtectedRoute userType="tenant">
            <BerichtenScreen />
          </ProtectedRoute>
        } />
        <Route path="/account" element={
          <ProtectedRoute userType="tenant">
            <AccountOverviewScreen />
          </ProtectedRoute>
        } />
        <Route path="/agenda" element={
          <ProtectedRoute userType="tenant">
            <TenantAgenda />
          </ProtectedRoute>
        } />
        <Route path="/mijn-pand" element={
          <ProtectedRoute userType="tenant">
            <MijnPandScreen />
          </ProtectedRoute>
        } />
        <Route path="/mijn-pand/:propertyId" element={
          <ProtectedRoute userType="tenant">
            <MijnPandScreen />
          </ProtectedRoute>
        } />
        <Route path="/contracten" element={
          <ProtectedRoute userType="tenant">
            <TenantContracts />
          </ProtectedRoute>
        } />
        <Route path="/bezichtigingen" element={
          <ProtectedRoute userType="tenant">
            <TenantViewings />
          </ProtectedRoute>
        } />
        <Route path="/contract/:contractId" element={
          <ProtectedRoute userType="tenant">
            <TenantContractDetail />
          </ProtectedRoute>
        } />
        <Route path="/contract/:contractId/amendment/:amendmentId" element={
          <ProtectedRoute userType="tenant">
            <AmendmentReview />
          </ProtectedRoute>
        } />
        {/* Landlord Routes - PRIVATE LANDLORDS ONLY */}
        {/* CRITICAL: These routes are ONLY for landlordType="private" */}
        {/* Agencies attempting to access will be redirected to /landlord/agency/dashboard */}
        <Route path="/landlord" element={
          <ProtectedRoute userType="landlord" landlordType="private">
            <VerhuurderOverviewScreen />
          </ProtectedRoute>
        } />
        <Route path="/landlord/contracts" element={
          <ProtectedRoute userType="landlord" landlordType="private">
            <LandlordContracts />
          </ProtectedRoute>
        } />
        <Route path="/landlord/verhuurde-panden" element={
          <ProtectedRoute userType="landlord" landlordType="private">
            <VerhuurdePanden />
          </ProtectedRoute>
        } />
        <Route path="/landlord/messages" element={
          <ProtectedRoute userType="landlord" landlordType="private">
            <LandlordMessages />
          </ProtectedRoute>
        } />
        <Route path="/landlord/profile" element={
          <ProtectedRoute userType="landlord" landlordType="private">
            <LandlordProfile />
          </ProtectedRoute>
        } />
        <Route path="/landlord/properties" element={
          <ProtectedRoute userType="landlord" landlordType="private">
            <LandlordProperties />
          </ProtectedRoute>
        } />
        <Route path="/landlord/viewings" element={
          <ProtectedRoute userType="landlord" landlordType="private">
            <LandlordViewings />
          </ProtectedRoute>
        } />
        <Route path="/landlord/viewings/:id" element={
          <ProtectedRoute userType="landlord" landlordType="private">
            <PropertyViewings />
          </ProtectedRoute>
        } />
        <Route path="/landlord/agenda" element={
          <ProtectedRoute userType="landlord" landlordType="private">
            <LandlordAgenda />
          </ProtectedRoute>
        } />
        {/* Private landlord onboarding - only for private landlords */}
        <Route path="/landlord/onboarding" element={
          <ProtectedRoute userType="landlord" landlordType="private">
            <LandlordOnboarding />
          </ProtectedRoute>
        } />
        <Route path="/landlord/add-property" element={
          <ProtectedRoute userType="landlord" landlordType="private">
            <SimpleAddProperty />
          </ProtectedRoute>
        } />
        <Route path="/landlord/edit-property/:id" element={
          <ProtectedRoute userType="landlord" landlordType="private">
            <EditProperty />
          </ProtectedRoute>
        } />
        <Route path="/landlord/crm" element={
          <ProtectedRoute userType="landlord" landlordType="private">
            <CRMIntegration />
          </ProtectedRoute>
        } />
        <Route path="/landlord/crm/preview" element={
          <ProtectedRoute userType="landlord" landlordType="private">
            <PropertyDetail />
          </ProtectedRoute>
        } />
        <Route path="/landlord/crm-guide" element={
          <ProtectedRoute userType="landlord" landlordType="private">
            <CRMGuide />
          </ProtectedRoute>
        } />
        <Route path="/landlord/property/:id" element={
          <ProtectedRoute userType="landlord" landlordType="private">
            <PropertyDetailLandlord />
          </ProtectedRoute>
        } />
        <Route path="/landlord/mijn-immo" element={
          <ProtectedRoute userType="landlord" landlordType="private">
            <MijnImmo />
          </ProtectedRoute>
        } />
        <Route path="/landlord/immo/:id" element={
          <ProtectedRoute userType="landlord" landlordType="private">
            <PrivateAgencyFollow />
          </ProtectedRoute>
        } />
        <Route path="/landlord/statistics" element={
          <ProtectedRoute userType="landlord" landlordType="private">
            <Statistics />
          </ProtectedRoute>
        } />
        <Route path="/landlord/billing" element={
          <ProtectedRoute userType="landlord" landlordType="private">
            <Billing />
          </ProtectedRoute>
        } />

        {/* AGENCY ROUTES - ONLY for landlordType="agency" */}
        {/* Private landlords attempting to access will be redirected to /landlord */}
        <Route path="/landlord/agency/dashboard" element={
          <ProtectedRoute userType="landlord" landlordType="agency">
            <AgencyOperationsDashboard />
          </ProtectedRoute>
        } />
        <Route path="/landlord/agency/clients/:clientLinkId" element={
          <ProtectedRoute userType="landlord" landlordType="agency">
            <ClientDashboard />
          </ProtectedRoute>
        } />
        <Route path="/landlord/agency/clients/:clientLinkId/add-property" element={
          <ProtectedRoute userType="landlord" landlordType="agency">
            <AgencyAddPropertyForClient />
          </ProtectedRoute>
        } />
        <Route path="/agency-invite/:inviteId" element={
          <AgencyInviteAcceptance />
        } />
        <Route path="/landlord/agency/onboarding" element={
          <ProtectedRoute userType="landlord" landlordType="agency">
            <AgencyOnboarding />
          </ProtectedRoute>
        } />
        <Route path="/landlord/agency/billing" element={
          <ProtectedRoute userType="landlord" landlordType="agency">
            <Billing />
          </ProtectedRoute>
        } />
        <Route path="/landlord/agency/profile" element={
          <ProtectedRoute userType="landlord" landlordType="agency">
            <LandlordProfile />
          </ProtectedRoute>
        } />
        <Route path="/landlord/agency/crm" element={
          <ProtectedRoute userType="landlord" landlordType="agency">
            <CRMIntegration />
          </ProtectedRoute>
        } />
        <Route path="/landlord/agency/crm/drafts/:integrationId/:draftId/edit" element={
          <ProtectedRoute userType="landlord" landlordType="agency">
            <CRMDraftEdit />
          </ProtectedRoute>
        } />
        <Route path="/landlord/agency/crm/preview" element={
          <ProtectedRoute userType="landlord" landlordType="agency">
            <PropertyDetail />
          </ProtectedRoute>
        } />
        <Route path="/landlord/agency/crm-guide" element={
          <ProtectedRoute userType="landlord" landlordType="agency">
            <CRMGuide />
          </ProtectedRoute>
        } />
        <Route path="/landlord/agency/properties" element={
          <ProtectedRoute userType="landlord" landlordType="agency">
            <AgencyProperties />
          </ProtectedRoute>
        } />
        <Route path="/landlord/agency/properties/new" element={
          <ProtectedRoute userType="landlord" landlordType="agency">
            <SimpleAddProperty />
          </ProtectedRoute>
        } />
        <Route path="/landlord/agency/properties/:propertyId" element={
          <ProtectedRoute userType="landlord" landlordType="agency">
            <PropertyDetailLandlord />
          </ProtectedRoute>
        } />
        <Route path="/landlord/agency/properties/:propertyId/edit" element={
          <ProtectedRoute userType="landlord" landlordType="agency">
            <EditProperty />
          </ProtectedRoute>
        } />
        <Route path="/landlord/agency/inbox" element={
          <ProtectedRoute userType="landlord" landlordType="agency">
            <LandlordMessages />
          </ProtectedRoute>
        } />
        <Route path="/landlord/agency/messages" element={
          <ProtectedRoute userType="landlord" landlordType="agency">
            <LandlordMessages />
          </ProtectedRoute>
        } />
        <Route path="/landlord/agency/viewings" element={
          <ProtectedRoute userType="landlord" landlordType="agency">
            <AgencyViewings />
          </ProtectedRoute>
        } />
        <Route path="/landlord/agency/agenda" element={
          <ProtectedRoute userType="landlord" landlordType="agency">
            <LandlordAgenda />
          </ProtectedRoute>
        } />
        <Route path="/landlord/agency/candidates" element={
          <ProtectedRoute userType="landlord" landlordType="agency">
            <AgencyCandidates />
          </ProtectedRoute>
        } />
        <Route path="/landlord/agency/clients" element={
          <ProtectedRoute userType="landlord" landlordType="agency">
            <AgencyDashboard />
          </ProtectedRoute>
        } />
        {/* Legacy route - redirect to new path */}
        <Route path="/landlord/agency-dashboard" element={
          <Navigate to="/landlord/agency/dashboard" replace />
        } />
        <Route path="/landlord/contracts/create" element={
          <ProtectedRoute userType="landlord" landlordType="private">
            <CreateContract />
          </ProtectedRoute>
        } />
        <Route path="/landlord/contracts/:id" element={
          <ProtectedRoute userType="landlord" landlordType="private">
            <ContractDetail />
          </ProtectedRoute>
        } />
        <Route path="/landlord/contracts/:id/edit" element={
          <ProtectedRoute userType="landlord" landlordType="private">
            <EditContract />
          </ProtectedRoute>
        } />
        <Route path="/landlord/contracts/:id/amend" element={
          <ProtectedRoute userType="landlord" landlordType="private">
            <AmendContract />
          </ProtectedRoute>
        } />
        <Route path="/landlord/contracts/:id/replace" element={
          <ProtectedRoute userType="landlord" landlordType="private">
            <ReplaceCustomContract />
          </ProtectedRoute>
        } />

        {/* Shared Routes */}
        <Route path="/auth/action" element={<AuthAction />} />
        <Route path="/property/:id" element={<PropertyDetail />} />
        <Route path="/p/:id" element={<ShortPropertyRedirect />} />
        <Route path="/chat/:chatId" element={
          <ProtectedRoute>
            <Chat />
          </ProtectedRoute>
        } />
        <Route path="/billing/success" element={
          <ProtectedRoute>
            <PaymentSuccess />
          </ProtectedRoute>
        } />
        <Route path="/billing/cancel" element={
          <ProtectedRoute>
            <PaymentCancel />
          </ProtectedRoute>
        } />
        <Route path="/legal" element={<LegalDocuments />} />
        <Route path="/legal/:policyId" element={<PolicyViewer />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/delete-account" element={
          <ProtectedRoute>
            <DeleteAccount />
          </ProtectedRoute>
        } />
        <Route
          path="*"
          element={
            <Navigate
              to={
                userType === 'tenant'
                  ? '/'
                  : landlordType === 'agency'
                    ? '/landlord/agency/dashboard'
                    : '/landlord'
              }
              replace
            />
          }
        />
      </Routes>
      </Suspense>
    </WebsiteLayout>
  );
}

function NotificationPermissionPrompt({ permission, isSupported, isInitializing, onEnable }) {
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!user?.uid) {
      setDismissed(false);
      return;
    }
    try {
      setDismissed(localStorage.getItem(`notificationsPromptDismissed:${user.uid}`) === '1');
    } catch {
      setDismissed(false);
    }
  }, [user?.uid]);

  if (!user || !isSupported || isInitializing || permission !== 'default' || dismissed) {
    return null;
  }

  return (
    <div className="fixed bottom-4 left-4 z-[130] max-w-sm rounded-2xl border border-slate-200 bg-white shadow-xl p-4">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-full bg-[#5EABEB]/12 text-[#2B6FA6] font-semibold">
          N
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-slate-900">Schakel meldingen in</p>
          <p className="mt-1 text-sm text-slate-600">
            Ontvang direct updates over berichten, contracten en bezichtigingen.
          </p>
          <div className="mt-3 flex gap-2">
            <button
              onClick={onEnable}
              className="h-10 rounded-lg bg-[#5EABEB] px-4 text-sm font-semibold text-white hover:bg-[#4A9FE6] transition-colors"
            >
              Inschakelen
            </button>
            <button
              onClick={() => {
                try {
                  localStorage.setItem(`notificationsPromptDismissed:${user.uid}`, '1');
                } catch {}
                setDismissed(true);
              }}
              className="h-10 rounded-lg border border-slate-200 px-4 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
            >
              Later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function AppContent() {
  // Initialize push notifications
  const notifications = useNotifications();

  return (
    <>
      <AppRoutes />
      <NotificationPermissionPrompt
        permission={notifications.permission}
        isSupported={notifications.isSupported}
        isInitializing={notifications.isInitializing}
        onEnable={notifications.enableNotifications}
      />
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
          },
          success: {
            duration: 3000,
            iconTheme: {
              primary: '#4ade80',
              secondary: '#fff',
            },
          },
          error: {
            duration: 5000,
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
        }}
      />
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <Router>
          <div className="App">
            <AppContent />
            <CookieConsent />
          </div>
        </Router>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;